import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  MapPin,
  MessageSquare,
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  HelpCircle,
  Clock,
  Building2,
  Send,
  GraduationCap,
  Headphones,
  Newspaper,
  ChevronDown,
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { useCms } from "@/lib/cms";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

const CARD_ICONS = { GraduationCap, Headphones, Newspaper, Sparkles, HelpCircle, Mail, Phone, MapPin, Clock, MessageSquare };

export default function Contact() {
  const { site, contact } = useCms();
  const [f, setF] = useState({
    firstName: "",
    lastName: "",
    email: "",
    topic: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  // Unchanged from before — same endpoint, same payload shape, same success/error handling.
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = {
        name: `${f.firstName} ${f.lastName}`.trim() || f.firstName || "Anonymous",
        email: f.email,
        subject: f.topic || "General inquiry",
        message: f.message,
      };
      await api.post("/contact", payload);
      toast.success(contact?.submit_success_message || "Message sent — we'll reply within one business day.");
      setF({ firstName: "", lastName: "", email: "", topic: "", message: "" });
    } catch {
      toast.error(contact?.submit_error_message || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  // SEO — same pattern as the podcast article page: set on mount, restore previous values on unmount.
  useEffect(() => {
    const prevTitle = document.title;
    if (contact?.seo_title) document.title = contact.seo_title;
    let meta = document.querySelector('meta[name="description"]');
    const prevDescription = meta?.getAttribute("content");
    if (meta && contact?.seo_meta_description) meta.setAttribute("content", contact.seo_meta_description);
    let metaKw = document.querySelector('meta[name="keywords"]');
    const prevKeywords = metaKw?.getAttribute("content");
    if (contact?.seo_meta_keywords) {
      if (!metaKw) {
        metaKw = document.createElement("meta");
        metaKw.setAttribute("name", "keywords");
        document.head.appendChild(metaKw);
      }
      metaKw.setAttribute("content", contact.seo_meta_keywords);
    }
    return () => {
      document.title = prevTitle;
      if (meta && prevDescription != null) meta.setAttribute("content", prevDescription);
      if (metaKw && prevKeywords != null) metaKw.setAttribute("content", prevKeywords);
    };
  }, [contact]);

  const email    = contact?.email_primary || site?.contact_email || "";
  const phone    = contact?.phone_primary || site?.contact_phone || "";
  const whatsapp = contact?.whatsapp || site?.whatsapp || "";
  const address  = [contact?.office_city, contact?.office_state].filter(Boolean).join(", ");
  const social   = site?.social || {};

  const setField = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div data-testid="contact-page" style={{ backgroundColor: "#F8FAFC" }}>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(59,104,231,0.08),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(69,184,172,0.08),transparent_50%)]" />
        {contact?.hero_media && (
          <img
            src={contact.hero_media.startsWith("http") ? contact.hero_media : `${BACKEND}${contact.hero_media}`}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-10"
            aria-hidden="true"
          />
        )}
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-20 sm:pt-28 pb-24 sm:pb-32">
          <motion.div
            variants={fadeUp} initial="hidden" animate="show" custom={0}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-6"
            style={{ backgroundColor: "#3B68E714", color: "#3B68E7" }}
          >
            <MessageSquare className="w-3.5 h-3.5" aria-hidden="true" /> {contact?.hero_badge || contact?.hero_subtitle || `Talk to ${site?.company_name || ""}`}
          </motion.div>
          <motion.h1
            variants={fadeUp} initial="hidden" animate="show" custom={1}
            className="rebrand-serif tracking-tight font-bold leading-[1.05] max-w-3xl"
            style={{ fontSize: "clamp(2.5rem, 5vw + 1rem, 4rem)", color: "#1A202C" }}
            data-testid="contact-headline"
          >
            {contact?.hero_title || "Let's start a conversation."}
          </motion.h1>
          <motion.p
            variants={fadeUp} initial="hidden" animate="show" custom={2}
            className="mt-6 text-base sm:text-lg leading-relaxed max-w-2xl"
            style={{ color: "#64748B" }}
          >
            {contact?.hero_description ||
              "Whether you're enrolling teachers, exploring an institutional partnership, or need help verifying a certificate — our team replies within one business day."}
          </motion.p>
          {contact?.hero_cta_text && (
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-8">
              <Link
                to={contact.hero_cta_link || "#"}
                className="inline-flex items-center gap-1.5 text-white text-sm font-semibold px-5 py-3 rounded-full transition-transform hover:scale-[1.02]"
                style={{ backgroundColor: "#3B68E7" }}
              >
                {contact.hero_cta_text} <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </motion.div>
          )}
        </div>
      </section>

      {/* Form + Info split */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.4fr,1fr] gap-6">
            {/* Form card */}
            <motion.div
              variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="bg-white p-6 sm:p-10 transition-shadow duration-300 hover:shadow-[0_14px_35px_rgba(0,0,0,0.07)]"
              style={{ borderRadius: 24, boxShadow: "0 10px 25px rgba(0,0,0,0.05)", border: "1px solid #E2E8F0" }}
              data-testid="contact-form-card"
            >
              <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: "#3B68E7" }}>{contact?.form_eyebrow || "Send us a message"}</div>
              <h2 className="rebrand-serif text-2xl sm:text-3xl font-bold" style={{ color: "#1A202C" }}>{contact?.form_title || "We read every message."}</h2>
              <p className="mt-2 text-sm" style={{ color: "#64748B" }}>{contact?.form_intro || "Expect a thoughtful reply, not an autoresponder."}</p>

              <form onSubmit={submit} className="mt-8 grid gap-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={contact?.label_first_name || "First name"} required>
                    <input
                      required
                      value={f.firstName}
                      onChange={setField("firstName")}
                      placeholder={contact?.placeholder_first_name || "Aditi"}
                      data-testid="contact-firstname"
                      className="w-full px-4 py-3.5 outline-none text-sm transition-all"
                      style={{ borderRadius: 16, border: "1px solid #E2E8F0" }}
                      onFocus={(e) => { e.target.style.borderColor = "#3B68E7"; e.target.style.boxShadow = "0 0 0 4px #3B68E71A"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; e.target.style.boxShadow = "none"; }}
                    />
                  </Field>
                  <Field label={contact?.label_last_name || "Last name"}>
                    <input
                      value={f.lastName}
                      onChange={setField("lastName")}
                      placeholder={contact?.placeholder_last_name || "Sharma"}
                      data-testid="contact-lastname"
                      className="w-full px-4 py-3.5 outline-none text-sm transition-all"
                      style={{ borderRadius: 16, border: "1px solid #E2E8F0" }}
                      onFocus={(e) => { e.target.style.borderColor = "#3B68E7"; e.target.style.boxShadow = "0 0 0 4px #3B68E71A"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; e.target.style.boxShadow = "none"; }}
                    />
                  </Field>
                </div>

                <Field label={contact?.label_email || "Work email"} required>
                  <input
                    required
                    type="email"
                    value={f.email}
                    onChange={setField("email")}
                    placeholder={contact?.placeholder_email || "you@school.edu"}
                    data-testid="contact-email"
                    className="w-full px-4 py-3.5 outline-none text-sm transition-all"
                    style={{ borderRadius: 16, border: "1px solid #E2E8F0" }}
                    onFocus={(e) => { e.target.style.borderColor = "#3B68E7"; e.target.style.boxShadow = "0 0 0 4px #3B68E71A"; }}
                    onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; e.target.style.boxShadow = "none"; }}
                  />
                </Field>

                <Field label={contact?.label_topic || "How can we help?"} required>
                  <select
                    required
                    value={f.topic}
                    onChange={setField("topic")}
                    data-testid="contact-topic"
                    className="w-full px-4 py-3.5 outline-none text-sm transition-all appearance-none bg-white"
                    style={{ borderRadius: 16, border: "1px solid #E2E8F0" }}
                    onFocus={(e) => { e.target.style.borderColor = "#3B68E7"; e.target.style.boxShadow = "0 0 0 4px #3B68E71A"; }}
                    onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; e.target.style.boxShadow = "none"; }}
                  >
                    <option value="" disabled>{contact?.placeholder_topic || "Select a topic…"}</option>
                    {(contact?.topics || []).filter((t) => t.visible !== false).map((t) => (
                      <option key={t.label} value={t.label}>{t.label}</option>
                    ))}
                  </select>
                </Field>

                <Field label={contact?.label_message || "Message"} required>
                  <textarea
                    required
                    value={f.message}
                    onChange={setField("message")}
                    placeholder={contact?.placeholder_message || "Tell us a little about your school and what you're hoping to solve…"}
                    data-testid="contact-message"
                    className="w-full px-4 py-3.5 outline-none text-sm min-h-36 resize-none transition-all"
                    style={{ borderRadius: 16, border: "1px solid #E2E8F0" }}
                    onFocus={(e) => { e.target.style.borderColor = "#3B68E7"; e.target.style.boxShadow = "0 0 0 4px #3B68E71A"; }}
                    onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; e.target.style.boxShadow = "none"; }}
                  />
                </Field>

                <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                  <p className="text-xs max-w-md" style={{ color: "#64748B" }}>
                    {contact?.consent_text || "By submitting this form, you agree to our"}{" "}
                    <Link to={contact?.privacy_link || "/privacy"} className="underline" style={{ color: "#64748B" }}>{contact?.privacy_text || "Privacy Policy"}</Link>.
                  </p>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit"
                    className="group inline-flex items-center gap-2 disabled:opacity-60 text-white text-sm font-semibold transition-transform hover:scale-[1.02]"
                    style={{ backgroundColor: "#3B68E7", borderRadius: 50, padding: "12px 24px", boxShadow: "0 4px 12px rgba(59,104,231,0.3)" }}
                  >
                    {sending ? (contact?.submit_loading_text || "Sending…") : (contact?.submit_text || "Send message")}
                    <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                  </button>
                </div>
              </form>
            </motion.div>

            {/* Side info card stack */}
            <div className="space-y-6">
              <motion.div
                variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={0}
                className="relative overflow-hidden p-8"
                style={{ borderRadius: 24, background: "linear-gradient(135deg, #3B68E7 0%, #2947B0 100%)" }}
                data-testid="contact-support-card"
              >
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.12),transparent_55%)]" />
                <div className="relative text-white">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-medium mb-5">
                    <Sparkles className="w-3 h-3" style={{ color: "#DDA756" }} aria-hidden="true" /> {contact?.support_title || "Direct support"}
                  </div>
                  <h3 className="rebrand-serif text-xl sm:text-2xl font-bold">{contact?.support_subtitle || "Reach the humans behind the platform."}</h3>

                  <div className="mt-6 space-y-5">
                    {(() => {
                      const rows = [];
                      if (contact?.support_cards?.length) {
                        contact.support_cards.filter((s) => s.visible !== false).forEach((s, i) => {
                          const Icon = CARD_ICONS[s.icon] || Sparkles;
                          const val = s.value || (s.icon === "Mail" ? email : s.icon === "Phone" ? phone : "");
                          if (!val && !s.link) return;
                          rows.push(<InfoRow key={i} icon={<Icon className="w-4 h-4" />} label={s.title} value={s.link ? <a href={s.link} target={s.link.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="text-white hover:underline">{val || s.link}</a> : <span className="text-white">{val}</span>} />);
                        });
                      } else {
                        if (email) rows.push(<InfoRow key="em" icon={<Mail className="w-4 h-4" />} label="Email" value={<a href={`mailto:${email}`} className="text-white hover:underline">{email}</a>} />);
                        if (phone) rows.push(<InfoRow key="ph" icon={<Phone className="w-4 h-4" />} label={`Call us · ${contact?.hours_weekday || "Mon–Fri"}`} value={<a href={`tel:${phone.replace(/\s+/g, "")}`} className="text-white hover:underline">{phone}</a>} />);
                        if (whatsapp) rows.push(<InfoRow key="wa" icon={<MessageSquare className="w-4 h-4" />} label="WhatsApp" value={<a href={`https://wa.me/${whatsapp.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="text-white hover:underline">{whatsapp}</a>} />);
                        if (address) rows.push(<InfoRow key="ad" icon={<MapPin className="w-4 h-4" />} label="Location" value={<span className="text-white">{address}</span>} />);
                      }
                      return rows;
                    })()}
                  </div>
                </div>
              </motion.div>

              <motion.div
                variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
                className="bg-white p-8 transition-shadow duration-300 hover:shadow-[0_14px_35px_rgba(0,0,0,0.07)]"
                style={{ borderRadius: 24, border: "1px solid #E2E8F0", boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}
                data-testid="contact-hq-card"
              >
                <div className="w-9 h-9 rounded-lg grid place-items-center mb-4" style={{ backgroundColor: "#EEF2FF", border: "1px solid #E0E7FF" }}>
                  <Building2 className="w-4.5 h-4.5" style={{ color: "#3B68E7" }} strokeWidth={2} aria-hidden="true" />
                </div>
                <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#3B68E7" }}>{contact?.hq_section_title || "Headquarters"}</div>
                <div className="mt-2 rebrand-serif text-lg leading-snug" style={{ color: "#1A202C" }}>
                  {contact?.hq_office_name || site?.company_name || ""}
                  {contact?.office_address && <><br />{contact.office_address}{contact?.office_landmark ? `, ${contact.office_landmark}` : ""}</>}
                  {contact?.office_address_2 && <><br />{contact.office_address_2}</>}
                  {(contact?.office_city || contact?.office_state) && <><br />{[contact?.office_city, contact?.office_state].filter(Boolean).join(", ")}{contact?.office_pin ? ` ${contact.office_pin}` : ""}</>}
                  {contact?.office_country && <><br />{contact.office_country}</>}
                </div>
                <a
                  href={contact?.map_link || (contact?.office_city ? `https://maps.google.com/?q=${encodeURIComponent([contact.office_address, contact.office_city].filter(Boolean).join(", "))}` : "#")} target="_blank" rel="noreferrer"
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium hover:underline" style={{ color: "#3B68E7" }}
                >
                  {contact?.hq_directions_text || "Get directions"} <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
                </a>
              </motion.div>

              {(contact?.offices || []).filter((o) => o.visible !== false).length > 0 && (
                <motion.div
                  variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1.5}
                  className="bg-white p-8 grid gap-4"
                  style={{ borderRadius: 24, border: "1px solid #E2E8F0", boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}
                  data-testid="contact-offices-card"
                >
                  <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#3B68E7" }}>Other offices</div>
                  {contact.offices.filter((o) => o.visible !== false).map((o, i) => (
                    <div key={i} className="text-sm" style={{ color: "#1A202C" }}>
                      <div className="font-semibold">{o.city}</div>
                      {o.address && <div style={{ color: "#64748B" }}>{o.address}</div>}
                      {o.contact && <div style={{ color: "#64748B" }}>{o.contact}</div>}
                    </div>
                  ))}
                </motion.div>
              )}

              <motion.div
                variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2}
                className="p-8" style={{ borderRadius: 24, backgroundColor: "#EAF6F5", border: "1px solid #D3EDEB" }}
                data-testid="contact-help-card"
              >
                <div className="w-9 h-9 rounded-lg bg-white grid place-items-center mb-4" style={{ border: "1px solid #D3EDEB" }}>
                  <HelpCircle className="w-4.5 h-4.5" style={{ color: "#3FB39E" }} strokeWidth={2} aria-hidden="true" />
                </div>
                <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#3FB39E" }}>{contact?.quick_answers_title || "Quick answers"}</div>
                <h3 className="mt-2 rebrand-serif text-lg leading-snug" style={{ color: "#1A202C" }}>{contact?.quick_answers_subtitle || "Looking for something specific?"}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "#64748B" }}>{contact?.quick_answers_description || "Check our FAQ for guides on booking, certificates, and account access."}</p>
                <Link to={contact?.quick_answers_link || "/faq"} data-testid="contact-help-link" className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium hover:underline" style={{ color: "#3FB39E" }}>
                  {contact?.quick_answers_button_text || "Visit Help Center"} <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                </Link>
              </motion.div>
              {whatsapp && (
                <motion.a href={`https://wa.me/${whatsapp.replace(/[^\d]/g,"")}`} target="_blank" rel="noreferrer"
                  variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
                  className="flex items-center gap-3 p-5 rounded-2xl border hover:shadow-md transition-shadow"
                  style={{ backgroundColor: "#25D366", borderColor: "#22C55E" }} data-testid="contact-whatsapp">
                  <div className="w-9 h-9 rounded-full bg-white/20 grid place-items-center shrink-0">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm">Chat on WhatsApp</div>
                    <div className="text-green-100 text-xs mt-0.5">{whatsapp}</div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-white ml-auto shrink-0" />
                </motion.a>
              )}
              {Object.values(social).some(Boolean) && (
                <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={3}
                  className="bg-white p-6" style={{ borderRadius: 24, border: "1px solid #E2E8F0", boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}
                  data-testid="contact-social">
                  <div className="text-xs uppercase tracking-wider font-semibold mb-4" style={{ color: "#3B68E7" }}>
                    {contact?.social_section_title || "Follow us"}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {social.facebook  && <SocialLink href={social.facebook}  label="Facebook"><svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg></SocialLink>}
                    {social.instagram && <SocialLink href={social.instagram} label="Instagram"><svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg></SocialLink>}
                    {social.linkedin  && <SocialLink href={social.linkedin}  label="LinkedIn"><svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg></SocialLink>}
                    {social.youtube   && <SocialLink href={social.youtube}   label="YouTube"><svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white"/></svg></SocialLink>}
                    {social.twitter   && <SocialLink href={social.twitter}   label="X/Twitter"><svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg></SocialLink>}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Contact cards */}
      {(contact?.cards || []).filter((c) => c.visible !== false).length > 0 && (
        <section className="pb-24">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {contact.cards.filter((c) => c.visible !== false).map((c, i) => {
                const Icon = CARD_ICONS[c.icon] || Sparkles;
                return (
                  <motion.div
                    key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                    className="bg-white p-6"
                    style={{ borderRadius: 24, border: "1px solid #E2E8F0", boxShadow: "0 10px 25px rgba(0,0,0,0.05)", backgroundColor: c.bg_color || "#FFFFFF" }}
                    data-testid={`contact-card-${i}`}
                  >
                    <div className="w-10 h-10 rounded-lg grid place-items-center mb-4" style={{ backgroundColor: "#EEF2FF" }}>
                      <Icon className="w-5 h-5" style={{ color: "#3B68E7" }} aria-hidden="true" />
                    </div>
                    <h3 className="rebrand-serif text-lg font-semibold" style={{ color: "#1A202C" }}>{c.title}</h3>
                    {c.description && <p className="mt-2 text-sm leading-relaxed" style={{ color: "#64748B" }}>{c.description}</p>}
                    {c.link && (
                      <a href={c.link} target={c.link.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium hover:underline" style={{ color: "#3B68E7" }}>
                        {c.button_text || "Learn more"} <ArrowRight className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Map */}
      {contact?.map_embed_url && (
        <section className="pb-24">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="overflow-hidden" style={{ borderRadius: 24, border: "1px solid #E2E8F0" }}>
              <iframe
                src={contact.map_embed_url}
                title="Office location"
                className="w-full h-80 sm:h-96"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </div>
        </section>
      )}

      {/* Office images */}
      {(contact?.office_images || []).length > 0 && (
        <section className="pb-24">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <h2 className="rebrand-serif text-2xl font-bold mb-6" style={{ color: "#1A202C" }}>Our office</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {contact.office_images.map((url, i) => (
                <div key={i} className="aspect-square overflow-hidden" style={{ borderRadius: 16 }}>
                  <img src={url.startsWith("http") ? url : `${BACKEND}${url}`} alt="" className="w-full h-full object-cover" data-testid={`office-image-${i}`} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {(contact?.faqs || []).filter((f) => f.visible !== false).length > 0 && (
        <section className="pb-24">
          <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
            <h2 className="rebrand-serif text-2xl font-bold mb-6" style={{ color: "#1A202C" }}>Frequently asked questions</h2>
            <div className="grid gap-3">
              {contact.faqs.filter((f) => f.visible !== false).map((f, i) => (
                <FaqItem key={i} question={f.question} answer={f.answer} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function SocialLink({ href, label, children }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" aria-label={label}
      className="w-10 h-10 rounded-xl grid place-items-center transition-all hover:scale-105"
      style={{ border: "1px solid #E2E8F0", color: "#64748B", backgroundColor: "#F8FAFC" }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor="#EEF2FF"; e.currentTarget.style.color="#3B68E7"; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor="#F8FAFC"; e.currentTarget.style.color="#64748B"; }}>
      {children}
    </a>
  );
}

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white" style={{ borderRadius: 16, border: "1px solid #E2E8F0" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
        aria-expanded={open}
        data-testid="faq-toggle"
      >
        <span className="text-sm font-medium" style={{ color: "#1A202C" }}>{question}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: "#64748B" }} />
      </button>
      {open && <p className="px-5 pb-4 text-sm leading-relaxed" style={{ color: "#64748B" }}>{answer}</p>}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#64748B" }}>
        {label}
        {required && <span style={{ color: "#DC2626" }} className="ml-0.5">*</span>}
      </div>
      {children}
    </label>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 shrink-0 rounded-lg bg-white/10 backdrop-blur border border-white/15 grid place-items-center text-white/80" aria-hidden="true">
        {icon}
      </div>
      <div className="text-sm">
        <div className="text-white/70 text-xs">{label}</div>
        <div className="mt-0.5 font-medium">{value}</div>
      </div>
    </div>
  );
}
